import { Trans } from "@lingui/react/macro";
import { LuMessageSquarePlus } from "react-icons/lu";
import {
  addToast,
  Button,
  Checkbox,
  Link,
  ModalBody,
  ModalContent,
  ModalFooter,
  ModalHeader,
  Radio,
  RadioGroup,
  useDisclosure,
} from "@heroui/react";
import { LimeModal } from "@/Components/NextBase/LimeModal";
import { LimeDatePicker } from "@/Components/NextBase/LimeDatePicker";
import { LimeCollapse } from "@/Components/NextBase/LimeCollapse";
import { useForm } from "@mantine/form";
import {
  DateValue,
  getLocalTimeZone,
  now,
  parseDate,
  toCalendarDateTime,
} from "@internationalized/date";
import { api, queryClient } from "@/lib/api-client";
import { useLingui } from "@lingui/react";
import { AxiosError } from "axios";
import dayjs from "dayjs";
import { t } from "@lingui/core/macro";
import { useMemo, useRef, useState } from "react";
import { LimeInput } from "@/Components/NextBase/LimeInput";
import {
  MarkdownEditor,
  type MarkdownEditorRef,
} from "@/Components/NextBase/LimeMarkdownEditor";
import { MergeTagChipRow } from "@/features/notification-settings/components/MergeTagChipRow";
import { insertAtCursor } from "@/features/notification-settings/utils/insertAtCursor";
import {
  expandForCharUsage,
  mergeTagGroupsForEvent,
} from "@/features/notification-settings/utils/mergeTags";
import { messageEditorExcludedActions } from "@/features/notification-settings/utils/messageEditorActions";
import { messageBudgetFor } from "@/features/notification-settings/utils/smsCounter";
import {
  flattenMarkdownLinks,
  htmlToSmsPlainText,
  plainTextToEditorHtml,
} from "@/features/notification-settings/utils/smsMarkup";
import { removeSmsEmoji } from "@/features/notification-settings/utils/transliterateForSms";
import type { NotificationEvent } from "@/server-types";
import { cn } from "@/utils";

type FormType = {
  channel: "sms" | "email";
  subject?: string;
  message: string;
  scheduledTime?: DateValue;
  isPromotional: boolean;
};

// Scheduled messages resolve merge tags against the appointment context at
// send time, so the appointment-scoped tag catalog applies; any appointment
// event yields that set, `appointmentReminder` is the canonical one.
// `{rescheduleFromDateTime}` is excluded — it only carries a value when a
// reschedule triggered the notification, never for a manually scheduled
// message.
const MERGE_TAG_EVENT: NotificationEvent = "appointmentReminder";
const EXCLUDED_MERGE_TAGS: ReadonlyArray<string> = ["{rescheduleFromDateTime}"];

// Offsets from "now" for the picker's quick presets — a scheduled message
// is most often meant to go out within the hour, which the calendar's
// date-first flow makes tedious to express.
const QUICK_SCHEDULE_MINUTES = [5, 10, 15, 30, 60] as const;

export const MessageModal = ({
  userAppointmentId,
}: {
  userAppointmentId: number;
}) => {
  const { isOpen, onOpen, onClose } = useDisclosure();
  const { i18n } = useLingui();

  const form = useForm<FormType>({
    initialValues: {
      channel: "sms",
      isPromotional: false,
      subject: undefined,
      message: "",
      scheduledTime: undefined,
    },
    validate: {
      channel: (value) =>
        value === "sms" || value === "email"
          ? null
          : t`Vrsta sporočila ni veljavna`,
      scheduledTime: (value) =>
        value && value.toDate(getLocalTimeZone()) > new Date()
          ? null
          : t`Čas mora biti v prihodnosti`,
      message: (value) =>
        value.length > 0 ? null : t`Polje sporočila je obvezno`,
      subject: (value) => {
        if (form.getValues().channel === "email") {
          return value && value.length > 0 ? null : t`Zadeva je obvezna`;
        }
        return null;
      },
    },
  });

  const {
    isOpen: templateOpened,
    onOpen: openTemplate,
    onClose: closeTemplate,
  } = useDisclosure();

  const { mutateAsync: createUserAppointmentMessageSchedule, isPending } =
    api.message.usePostUserAppointmentMessageSchedule();

  const { data: templatesData } = api.message.useGetMessageTemplate({
    channel: form.values.channel,
  });

  const language = i18n.locale;

  const subjectRef = useRef<HTMLInputElement>(null);
  const editorRef = useRef<MarkdownEditorRef | null>(null);
  const [focusedField, setFocusedField] = useState<"subject" | "body">("body");
  // The editor's `value` prop is a seed, not a controlled value — updating
  // it on every keystroke would make TipTap reset the cursor (see
  // ContentEditorDrawer). It changes only on external transitions: modal
  // open, template apply, channel switch.
  const [editorSeed, setEditorSeed] = useState("");

  const tagOptions = useMemo(
    () =>
      mergeTagGroupsForEvent(MERGE_TAG_EVENT).flatMap((g) =>
        g.items
          .filter((item) => !EXCLUDED_MERGE_TAGS.includes(item.tag))
          .map((item) => ({ value: item.tag, label: i18n._(item.label) })),
      ),
    [i18n],
  );

  // Routes a merge-tag click to whatever is focused: the email subject
  // input gets a cursor insert, everything else goes to the body editor
  // as a chip (serialized back to `{tag}` text by the editor's onChange).
  function handleTagInsert(tag: string) {
    if (form.values.channel === "email" && focusedField === "subject") {
      const subject = form.values.subject ?? "";
      const el = subjectRef.current;
      if (!el) {
        form.setFieldValue("subject", subject + tag);
        return;
      }
      const { selectionStart, selectionEnd } = el;
      const { value: next, cursor } = insertAtCursor(
        subject,
        tag,
        selectionStart ?? subject.length,
        selectionEnd ?? subject.length,
      );
      form.setFieldValue("subject", next);
      queueMicrotask(() => {
        const node = subjectRef.current;
        if (!node) return;
        node.focus();
        node.setSelectionRange(cursor, cursor);
      });
      return;
    }
    const editor = editorRef.current;
    if (!editor) {
      form.setFieldValue("message", form.values.message + tag);
      return;
    }
    editor.commands.insertNotificationTag(tag);
  }

  // Each `{tag}` counts as its worst-case expansion so the segment
  // estimate doesn't understate what the resolved message will consume
  // (DEV-8042 semantics; no tenant data here, so shared defaults apply).
  const smsBudget = messageBudgetFor(
    "sms",
    expandForCharUsage(form.values.message, {}),
  );
  const smsCharCount = smsBudget.charCount;
  const smsCapacity = smsBudget.capacity;
  const smsParts = smsBudget.parts;
  const smsOverLimit = smsBudget.isOverLimit;

  const isEmail = form.values.channel === "email";

  function scheduleInMinutes(minutes: number) {
    form.setFieldValue(
      "scheduledTime",
      toCalendarDateTime(now(getLocalTimeZone()).add({ minutes })),
    );
  }

  const [pendingAction, setPendingAction] = useState<"now" | "schedule" | null>(
    null,
  );

  const handleSubmit = async (values: FormType, sendNow: boolean) => {
    if (!sendNow && !values.scheduledTime) return;

    const payload: {
      userAppointmentId: number;
      message: string;
      subject?: string;
      channel: "email" | "sms";
      scheduledTime?: string;
      isPromotional: boolean;
    } = {
      userAppointmentId,
      message: values.message,
      subject: values.subject,
      channel: values.channel,
      isPromotional: values.isPromotional,
      scheduledTime:
        sendNow || !values.scheduledTime
          ? undefined
          : dayjs(values.scheduledTime.toDate(getLocalTimeZone())).format(
              "YYYY-MM-DDTHH:mm",
            ),
    };

    setPendingAction(sendNow ? "now" : "schedule");

    await createUserAppointmentMessageSchedule(payload)
      .then(() => {
        addToast({
          title: sendNow
            ? t`Sporočilo bo poslano`
            : t`Sporočilo je bilo uspešno načrtovano`,
          color: "success",
        });

        onClose?.();

        queryClient.invalidateQueries({
          queryKey: ["userAppointmentMessages", { userAppointmentId }],
        });
      })
      .catch((e) =>
        addToast({
          title: t`Napaka`,
          description:
            e instanceof AxiosError
              ? (e.response?.data?.error ?? e.message)
              : t`Prišlo je do napake`,
          color: "danger",
        }),
      )
      .finally(() => setPendingAction(null));
  };

  // Send-now ignores the scheduled-time picker, so validate only the fields
  // that gate an immediate send.
  function handleSendNow() {
    const invalid = (["channel", "message", "subject"] as const).some(
      (field) => form.validateField(field).hasError,
    );
    if (invalid) return;
    handleSubmit(form.getValues(), true);
  }

  const applyTemplate = (templateId: number) => {
    if (!templatesData) return;

    const templateToApply = templatesData.templates.find(
      (template) => template.id === templateId,
    );

    if (!templateToApply) return;

    // Fall back to the first localization that actually has content —
    // matching the picker preview, which uses the same predicate.
    const rawMessageToApply =
      templateToApply.MessageTemplateLocalized.find(
        (ml) => ml.language === language,
      )?.message ||
      templateToApply.MessageTemplateLocalized.find((ml) => ml.message.length)
        ?.message ||
      "";

    // Templates bypass the editor's typed/pasted-input emoji filter, so
    // sanitize here — same normalization as `handleChannelChange`.
    const messageToApply =
      form.values.channel === "sms"
        ? removeSmsEmoji(rawMessageToApply)
        : rawMessageToApply;

    form.setFieldValue("message", messageToApply);
    setEditorSeed(
      plainTextToEditorHtml(messageToApply, { preserveLinks: isEmail }),
    );

    if (form.values.channel === "email") {
      const subjectToApply =
        templateToApply.MessageTemplateLocalized.find(
          (ml) => ml.language === language,
        )?.subject ||
        templateToApply.MessageTemplateLocalized.find(
          (ml) => ml.subject?.length,
        )?.subject;

      form.setFieldValue("subject", subjectToApply || undefined);
    }

    closeTemplate();
  };

  function handleOpen() {
    // The modal unmounts its children on close while the form keeps its
    // draft — reseed the editor so a reopened modal shows the draft, and
    // reset the tag-insertion target (a stale "subject" focus from a
    // previous session would route chips into an unfocused field).
    setFocusedField("body");
    setEditorSeed(
      plainTextToEditorHtml(form.values.message, { preserveLinks: isEmail }),
    );
    onOpen();
  }

  function handleChannelChange(value: string) {
    const channel = value as "sms" | "email";
    setFocusedField("body");
    // Outbound SMS strip emoji (they flip the body to UCS-2), so drop
    // them from a draft brought over from email — the editor and counter
    // then show what will actually be sent.
    // Markdown links only render as links on the email side, so flatten
    // them to "label: url" rather than let the syntax reach a handset.
    const message =
      channel === "sms"
        ? removeSmsEmoji(flattenMarkdownLinks(form.values.message))
        : form.values.message;
    if (message !== form.values.message) {
      form.setFieldValue("message", message);
    }
    form.setFieldValue("channel", channel);
    setEditorSeed(
      plainTextToEditorHtml(message, { preserveLinks: channel === "email" }),
    );
  }

  return (
    <>
      <Button
        onPress={handleOpen}
        variant="light"
        color="primary"
        startContent={<LuMessageSquarePlus size={16} />}
        size="sm"
      >
        <Trans>Ustvari novo</Trans>
      </Button>
      <LimeModal isOpen={isOpen} onClose={onClose} size="3xl">
        <form
          onSubmit={(e) => {
            e.preventDefault();
            e.stopPropagation();

            form.validate();

            if (form.isValid()) {
              const values = form.getValues();
              handleSubmit(values, false);
            }
          }}
        >
          <ModalContent>
            <ModalHeader>
              <Trans>Načrtuj sporočilo</Trans>
            </ModalHeader>
            <ModalBody>
              <div className="flex flex-col gap-2">
                <RadioGroup
                  label={t`Vrsta sporočila`}
                  {...form.getInputProps("channel")}
                  value={form.values.channel}
                  onValueChange={handleChannelChange}
                >
                  <Radio value={"sms"}>SMS</Radio>
                  <Radio value={"email"}>E-mail</Radio>
                </RadioGroup>

                {templatesData?.templates.length ? (
                  <Button
                    variant="faded"
                    onPress={openTemplate}
                    className="w-fit"
                    size="sm"
                    type="button"
                  >
                    <Trans>Uporabi predlogo</Trans>
                  </Button>
                ) : (
                  <div className="flex items-center gap-2">
                    <p className="text-sm">
                      <Trans>Nimate še dodanih predlog.</Trans>
                    </p>

                    <Link
                      href={"/dashboard/notifications/templates/create"}
                      showAnchorIcon
                      className="text-sm"
                    >
                      <Trans>Dodaj novo</Trans>
                    </Link>
                  </div>
                )}

                <LimeCollapse isOpen={templateOpened}>
                  <div className="flex flex-col">
                    <p className="text-sm font-medium">
                      <Trans>Predloge</Trans>
                    </p>

                    {templatesData && (
                      <div className="flex flex-row flex-wrap gap-2">
                        {templatesData.templates.map((template, index) => (
                          <button
                            className="flex min-h-6 cursor-pointer items-center justify-center rounded-lg border-none bg-[#eef4ef] px-2 py-1 text-left text-[#68A379] outline-hidden"
                            key={template.id}
                            onClick={() => applyTemplate(template.id)}
                            type="button"
                          >
                            <p className="text-xs">
                              {template.channel === "email" ? (
                                <>
                                  {template.MessageTemplateLocalized.find(
                                    (tl) => tl.language === language,
                                  )?.subject ||
                                    template.MessageTemplateLocalized.find(
                                      (tl) => tl.subject?.length,
                                    )?.subject ||
                                    undefined}{" "}
                                  <br></br>
                                </>
                              ) : undefined}
                              {template.MessageTemplateLocalized.find(
                                (tl) => tl.language === language,
                              )?.message ||
                                template.MessageTemplateLocalized.find(
                                  (tl) => tl.message.length,
                                )?.message ||
                                ""}
                            </p>
                          </button>
                        ))}
                      </div>
                    )}
                  </div>
                </LimeCollapse>

                <LimeCollapse isOpen={form.values.channel === "email"}>
                  <LimeInput
                    ref={subjectRef}
                    label={t`Zadeva`}
                    {...form.getInputProps("subject")}
                    onFocus={() => setFocusedField("subject")}
                  ></LimeInput>
                </LimeCollapse>

                <div className="flex flex-col gap-1">
                  <p className="text-foreground-600 text-sm">
                    <Trans>Sporočilo</Trans>
                  </p>
                  <div onFocus={() => setFocusedField("body")}>
                    {/*
                      Same chips-only setup as the notification-settings
                      editor (DEV-8041): merge tags render as chips, and
                      the body is stored as plain text on the wire for
                      both channels. Formatting actions stay excluded
                      except the email channel's link action, which
                      serializes to `[label](url)` and is rendered back to
                      an anchor when the email goes out.
                      The `key` remounts the editor on channel switch —
                      the emoji filter is wired into `editorProps` at
                      creation time only.
                    */}
                    <MarkdownEditor
                      key={form.values.channel}
                      ref={editorRef}
                      value={editorSeed}
                      onChange={(html) =>
                        form.setFieldValue(
                          "message",
                          htmlToSmsPlainText(html, { preserveLinks: isEmail }),
                        )
                      }
                      dataIdentifier="appointment-message-body"
                      filterInsertedText={
                        form.values.channel === "sms"
                          ? removeSmsEmoji
                          : undefined
                      }
                      excludeActions={messageEditorExcludedActions(
                        form.values.channel,
                      )}
                      tagOptions={tagOptions}
                    />
                  </div>
                  {form.errors.message && (
                    <p className="text-tiny text-danger">
                      {form.errors.message}
                    </p>
                  )}
                  {form.values.channel === "sms" && (
                    <p
                      className={cn(
                        "text-[11px]",
                        smsOverLimit ? "text-danger" : "text-default-500",
                      )}
                    >
                      <Trans>
                        {smsCharCount} / {smsCapacity} znakov · {smsParts} SMS
                      </Trans>
                    </p>
                  )}
                  <div className="mt-1">
                    <p className="text-default-500 mb-1 text-[11px] font-semibold tracking-wider uppercase">
                      <Trans>Vstavi spremenljivko</Trans>
                    </p>
                    <MergeTagChipRow
                      event={MERGE_TAG_EVENT}
                      excludeTags={EXCLUDED_MERGE_TAGS}
                      onInsert={handleTagInsert}
                    />
                  </div>
                </div>

                <Checkbox
                  isSelected={form.values.isPromotional}
                  onValueChange={(value) =>
                    form.setFieldValue("isPromotional", value)
                  }
                  data-identifier="scheduled-message-promotional"
                >
                  <span className="flex flex-col">
                    <span className="text-sm">
                      <Trans>To je oglasno sporočilo</Trans>
                    </span>
                    <span className="text-default-500 text-xs">
                      <Trans>
                        Označite, če sporočilo vsebuje ponudbo ali akcijo. Takih
                        sporočil ne pošljemo strankam, ki so se odjavile od
                        ponudb, in jim dodamo povezavo za odjavo.
                      </Trans>
                    </span>
                  </span>
                </Checkbox>

                <LimeDatePicker
                  label={t`Datum in ura pošiljanja (za načrtovanje)`}
                  {...form.getInputProps("scheduledTime")}
                  granularity="minute"
                  variant="bordered"
                  showMonthAndYearPickers
                  minValue={parseDate(dayjs().format("YYYY-MM-DD"))}
                  CalendarBottomContent={
                    <div className="flex flex-col gap-2 px-2 pb-2">
                      <div className="flex flex-wrap gap-1">
                        {QUICK_SCHEDULE_MINUTES.map((minutes) => (
                          <Button
                            key={minutes}
                            type="button"
                            size="sm"
                            variant="flat"
                            onPress={() => scheduleInMinutes(minutes)}
                          >
                            {minutes < 60
                              ? t`Čez ${minutes} min`
                              : t`Čez ${minutes / 60} h`}
                          </Button>
                        ))}
                      </div>
                      <Button
                        type="button"
                        size="sm"
                        variant="flat"
                        color="primary"
                        className="w-full"
                        onPress={() => scheduleInMinutes(0)}
                      >
                        <Trans>Ponastavi na trenutni čas</Trans>
                      </Button>
                    </div>
                  }
                />
              </div>
            </ModalBody>
            <ModalFooter>
              <Button
                isLoading={pendingAction === "now"}
                isDisabled={isPending}
                type="button"
                variant="flat"
                color="primary"
                onPress={handleSendNow}
              >
                <Trans>Pošlji zdaj</Trans>
              </Button>
              <Button
                isLoading={pendingAction === "schedule"}
                isDisabled={isPending}
                type="submit"
                color="primary"
              >
                <Trans>Načrtuj sporočilo</Trans>
              </Button>
            </ModalFooter>
          </ModalContent>
        </form>
      </LimeModal>
    </>
  );
};
